import type { GatewayBrowserClient } from "../gateway.ts";
import type { UiSettings } from "../storage.ts";
import type { LocalResourcesHistoryPoint, LocalResourcesSnapshot, LogEntry } from "../types.ts";
import type { LocalLlmViewProps } from "../views/local-llm.ts";
import { syncLocalLlmPollInterval } from "../app-polling.ts";
import {
  appendConsoleLines,
  buildActivityLines,
  formatGatewayLogLine,
} from "../local-llm/activity-lines.ts";
import { formatLocalResourcesError } from "./llm-stats-capacity.ts";
import { parseLogLine } from "./logs.ts";

const CONSOLE_MAX_LINES = 500;
const LOG_FETCH_LIMIT = 80;
const LOG_LOOKBACK_MS = 30_000;
const HISTORY_MAX = 30;

export type LocalLlmState = {
  client: GatewayBrowserClient | null;
  connected: boolean;
  localLlmLoading: boolean;
  localLlmError: string | null;
  localLlmSnapshot: LocalResourcesSnapshot | null;
  localLlmPrevSnapshot: LocalResourcesSnapshot | null;
  localLlmHistory: LocalResourcesHistoryPoint[];
  localLlmConsoleLines: string[];
  localLlmLastLogsFetchAt: number | null;
  localLlmLogsTailSupported: boolean;
  settings: {
    localLlmSelectedModel: string | null;
    localLlmAutoRefresh: boolean;
    localLlmIncludeGatewayLogs: boolean;
  };
};

export type LocalLlmViewHost = LocalLlmState & {
  applySettings: (next: UiSettings) => void;
  settings: UiSettings;
};

export function buildLocalLlmViewProps(state: LocalLlmViewHost): LocalLlmViewProps {
  return {
    loading: state.localLlmLoading,
    error: state.localLlmError,
    snapshot: state.localLlmSnapshot,
    consoleText: state.localLlmConsoleLines.join("\n"),
    thinkingPreview: state.localLlmSnapshot?.ollamaLiveActivity.thinkingPreview ?? null,
    selectedModel: state.settings.localLlmSelectedModel,
    history: state.localLlmHistory,
    autoRefresh: state.settings.localLlmAutoRefresh,
    includeGatewayLogs: state.settings.localLlmIncludeGatewayLogs,
    connected: state.connected,
    onRefresh: () => void refreshLocalLlm(state),
    onClearConsole: () => clearLocalLlmConsole(state),
    onSelectModel: (model) => {
      state.applySettings({
        ...state.settings,
        localLlmSelectedModel: model,
      });
      void refreshLocalLlm(state);
    },
    onAutoRefreshChange: (next) => {
      state.applySettings({ ...state.settings, localLlmAutoRefresh: next });
    },
    onIncludeGatewayLogsChange: (next) => {
      state.applySettings({ ...state.settings, localLlmIncludeGatewayLogs: next });
    },
  };
}

function isUnsupportedLogsTailFiltersError(err: unknown): boolean {
  const message = String(err);
  return message.includes("invalid logs.tail params") && message.includes("unexpected property");
}

export function clearLocalLlmConsole(state: LocalLlmState): void {
  state.localLlmConsoleLines = [];
  state.localLlmLastLogsFetchAt = null;
}

function appendHistory(
  history: LocalResourcesHistoryPoint[],
  snapshot: LocalResourcesSnapshot,
): LocalResourcesHistoryPoint[] {
  const point: LocalResourcesHistoryPoint = {
    collectedAtMs: snapshot.collectedAtMs,
    cpuPercent: snapshot.host.cpuPercent,
    memUsedPercent: snapshot.host.memUsedPercent,
  };
  return [...history, point].slice(-HISTORY_MAX);
}

export async function refreshLocalLlm(
  state: LocalLlmState,
  opts?: { quiet?: boolean },
): Promise<void> {
  if (!state.client || !state.connected) {
    return;
  }
  if (state.localLlmLoading && !opts?.quiet) {
    return;
  }
  if (!opts?.quiet) {
    state.localLlmLoading = true;
  }
  state.localLlmError = null;

  const previous = state.localLlmSnapshot;
  const selectedModel = state.settings.localLlmSelectedModel?.trim() || null;

  try {
    const snapshot = await state.client.request<LocalResourcesSnapshot>("resources.local", {});
    const activity = buildActivityLines({
      snapshot,
      previous,
      selectedModel,
    });
    state.localLlmConsoleLines = appendConsoleLines(
      state.localLlmConsoleLines,
      activity,
      CONSOLE_MAX_LINES,
    );
    state.localLlmPrevSnapshot = previous;
    state.localLlmSnapshot = snapshot;
    state.localLlmHistory = appendHistory(state.localLlmHistory, snapshot);

    if (snapshot.ollamaLiveActivity.lines.length > 0) {
      const recentKeys = new Set(state.localLlmConsoleLines.slice(-200));
      const liveLines = snapshot.ollamaLiveActivity.lines.filter((line) => !recentKeys.has(line));
      if (liveLines.length > 0) {
        state.localLlmConsoleLines = appendConsoleLines(
          state.localLlmConsoleLines,
          liveLines,
          CONSOLE_MAX_LINES,
        );
      }
    }

    if (state.settings.localLlmIncludeGatewayLogs) {
      await appendGatewayLogLines(state);
    }

    syncLocalLlmPollInterval(state as unknown as Parameters<typeof syncLocalLlmPollInterval>[0]);
  } catch (err) {
    state.localLlmError = formatLocalResourcesError(err);
    const stamp = new Date().toLocaleTimeString();
    state.localLlmConsoleLines = appendConsoleLines(
      state.localLlmConsoleLines,
      [`[${stamp}] refresh failed: ${state.localLlmError}`],
      CONSOLE_MAX_LINES,
    );
    if (!opts?.quiet) {
      state.localLlmSnapshot = null;
    }
  } finally {
    if (!opts?.quiet) {
      state.localLlmLoading = false;
    }
  }
}

async function appendGatewayLogLines(state: LocalLlmState): Promise<void> {
  if (!state.client) {
    return;
  }
  const now = Date.now();
  const sinceMs = state.localLlmLastLogsFetchAt ?? now - LOG_LOOKBACK_MS;
  const useServerFilters = state.localLlmLogsTailSupported !== false;

  const params = {
    limit: LOG_FETCH_LIMIT,
    maxBytes: 512_000,
    ...(useServerFilters ? { needle: "ollama", sinceMs } : {}),
  };

  try {
    let res: unknown;
    try {
      res = await state.client.request("logs.tail", params);
    } catch (err) {
      if (useServerFilters && isUnsupportedLogsTailFiltersError(err)) {
        state.localLlmLogsTailSupported = false;
        res = await state.client.request("logs.tail", {
          limit: LOG_FETCH_LIMIT,
          maxBytes: 512_000,
        });
      } else {
        throw err;
      }
    }

    const payload = res as { lines?: unknown };
    const rawLines = Array.isArray(payload.lines)
      ? payload.lines.filter((line): line is string => typeof line === "string")
      : [];
    const entries = rawLines.map(parseLogLine);
    const ollamaEntries = useServerFilters
      ? entries
      : entries.filter((entry) => lineMentionsOllama(entry));

    const recentKeys = new Set(state.localLlmConsoleLines.slice(-200));
    const logLines: string[] = [];
    for (const entry of ollamaEntries) {
      const formatted = formatGatewayLogLine(entry);
      if (!recentKeys.has(formatted)) {
        logLines.push(formatted);
        recentKeys.add(formatted);
      }
    }
    if (logLines.length > 0) {
      state.localLlmConsoleLines = appendConsoleLines(
        state.localLlmConsoleLines,
        logLines,
        CONSOLE_MAX_LINES,
      );
    }
    state.localLlmLastLogsFetchAt = now;
  } catch (err) {
    const stamp = new Date().toLocaleTimeString();
    state.localLlmConsoleLines = appendConsoleLines(
      state.localLlmConsoleLines,
      [`[${stamp}] gateway logs: ${String(err)}`],
      CONSOLE_MAX_LINES,
    );
  }
}

function lineMentionsOllama(entry: LogEntry): boolean {
  const haystack = `${entry.message ?? ""} ${entry.raw} ${entry.subsystem ?? ""}`.toLowerCase();
  return haystack.includes("ollama");
}
